import React, { useState, useEffect } from 'react';
import { 
  Package, Search, Plus, Minus, AlertTriangle, ArrowUpDown,
  Sun, Zap, Battery, Car, Settings2, Wrench, History, 
  TrendingUp, TrendingDown, Edit, RefreshCw, Loader2, Download, Bell
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { stockAPI, installationProductAPI } from '../lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

const CATEGORIES = [
  { value: 'zonnepanelen', label: 'Zonnepanelen', icon: Sun, color: 'text-amber-500' },
  { value: 'omvormers', label: 'Omvormers', icon: Zap, color: 'text-blue-500' },
  { value: 'batterijen', label: 'Batterijen', icon: Battery, color: 'text-green-500' },
  { value: 'laadpalen', label: 'Laadpalen', icon: Car, color: 'text-purple-500' },
  { value: 'montagemateriaal', label: 'Montagemateriaal', icon: Settings2, color: 'text-gray-500' },
  { value: 'werken', label: 'Werken', icon: Wrench, color: 'text-orange-500' },
];

const Stock = () => {
  const [stockItems, setStockItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  
  // Movement dialog
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [movementType, setMovementType] = useState('in');
  const [movementQuantity, setMovementQuantity] = useState(1);
  const [movementNotes, setMovementNotes] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Stock edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  
  // History dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [movements, setMovements] = useState([]);
  const [loadingMovements, setLoadingMovements] = useState(false);

  useEffect(() => {
    fetchData();
  }, [showLowStockOnly]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [stockRes, productsRes, summaryRes] = await Promise.all([
        stockAPI.getAll(showLowStockOnly),
        installationProductAPI.getAll(),
        stockAPI.getSummary()
      ]);
      setStockItems(stockRes.data);
      setProducts(productsRes.data.filter(p => p.is_active));
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Kon voorraad niet laden');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    if (!cat) return Package;
    return cat.icon;
  };

  const getCategoryColor = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat?.color || 'text-gray-500';
  };

  const handleOpenMovement = (product, type) => {
    setSelectedProduct(product);
    setMovementType(type);
    setMovementQuantity(1);
    setMovementNotes('');
    setMovementDialogOpen(true);
  };

  const handleSaveMovement = async () => {
    if (!selectedProduct || movementQuantity <= 0) {
      toast.error('Vul een geldig aantal in');
      return;
    }

    setSaving(true);
    try {
      await stockAPI.recordMovement({
        product_id: selectedProduct.product_id,
        movement_type: movementType,
        quantity: movementQuantity,
        notes: movementNotes || null
      });
      
      toast.success(movementType === 'in' ? 'Voorraad verhoogd' : 'Voorraad verlaagd');
      setMovementDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon beweging niet registreren');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEdit = (item) => {
    setEditingStock({
      product_id: item.product_id,
      quantity: item.quantity || 0,
      min_quantity: item.min_quantity || 0,
      location: item.location || 'Magazijn',
      notes: item.notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingStock) return;

    setSaving(true);
    try {
      await stockAPI.createOrUpdate(editingStock);
      toast.success('Voorraad bijgewerkt');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Kon voorraad niet bijwerken');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenHistory = async (item) => {
    setHistoryProduct(item);
    setHistoryDialogOpen(true);
    setLoadingMovements(true);
    
    try {
      const res = await stockAPI.getMovements(item.product_id);
      setMovements(res.data);
    } catch (error) {
      toast.error('Kon geschiedenis niet laden');
    } finally {
      setLoadingMovements(false);
    }
  };

  // Get products not yet in stock
  const productsNotInStock = products.filter(
    p => !stockItems.find(s => s.product_id === p.id)
  );

  const handleAddProductToStock = async (product) => {
    setSaving(true);
    try {
      await stockAPI.createOrUpdate({
        product_id: product.id,
        quantity: 0,
        min_quantity: 5,
        location: 'Magazijn'
      });
      toast.success(`${product.name} toegevoegd aan voorraad`);
      fetchData();
    } catch (error) {
      toast.error('Kon product niet toevoegen');
    } finally {
      setSaving(false);
    }
  };

  // Filter stock items
  const filteredItems = stockItems.filter(item => {
    const matchesSearch = 
      item.product_name?.toLowerCase().includes(search.toLowerCase()) ||
      item.product_brand?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = activeCategory === 'all' || item.product_category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'd MMM yyyy HH:mm', { locale: nl });
    } catch {
      return dateString;
    }
  };

  // Export stock to CSV
  const handleExport = async () => {
    try {
      const res = await stockAPI.exportStock();
      const data = res.data;
      
      if (!data || data.length === 0) {
        toast.error('Geen voorraad om te exporteren');
        return;
      }
      
      // Create CSV content
      const headers = ['Product', 'Merk', 'Categorie', 'Voorraad', 'Minimum', 'Eenheid', 'Locatie', 'Laatst bijgewerkt'];
      const rows = data.map(item => [
        item.product_name,
        item.brand || '',
        item.category || '',
        item.quantity,
        item.min_quantity,
        item.unit,
        item.location || '',
        item.updated_at || ''
      ]);
      
      const csvContent = [headers, ...rows]
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n');
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `voorraad_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      link.click();
      
      toast.success('Voorraad geëxporteerd');
    } catch (error) {
      toast.error('Kon voorraad niet exporteren');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="stock-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Voorraad</h1>
          <p className="text-muted-foreground mt-1">Beheer je productvoorraad</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleExport} variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Exporteren
          </Button>
          <Button onClick={() => setShowLowStockOnly(!showLowStockOnly)} variant={showLowStockOnly ? 'default' : 'outline'} size="sm">
            <AlertTriangle className="w-4 h-4 mr-2" />
            {showLowStockOnly ? 'Alle tonen' : 'Lage voorraad'}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-blue-500/20 flex items-center justify-center">
                  <Package className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.total_items}</p>
                  <p className="text-xs text-muted-foreground">Producten in voorraad</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={summary.low_stock_count > 0 ? 'border-amber-500/50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-amber-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-500">{summary.low_stock_count}</p>
                  <p className="text-xs text-muted-foreground">Lage voorraad</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={summary.out_of_stock_count > 0 ? 'border-red-500/50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-red-500/20 flex items-center justify-center">
                  <Package className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-500">{summary.out_of_stock_count}</p>
                  <p className="text-xs text-muted-foreground">Niet op voorraad</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-green-500/20 flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.recent_movements?.length || 0}</p>
                  <p className="text-xs text-muted-foreground">Recente bewegingen</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="stock" className="space-y-4">
        <TabsList>
          <TabsTrigger value="stock">Voorraad</TabsTrigger>
          <TabsTrigger value="alerts" className="relative">
            Alerts
            {summary && (summary.low_stock_count + summary.out_of_stock_count) > 0 && (
              <Badge variant="destructive" className="ml-1 px-1.5 py-0 text-xs">
                {summary.low_stock_count + summary.out_of_stock_count}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="add">Producten Toevoegen</TabsTrigger>
        </TabsList>

        <TabsContent value="stock" className="space-y-4">
          {/* Search & Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Zoek product..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={activeCategory} onValueChange={setActiveCategory}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Categorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle categorieën</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    <span className="flex items-center gap-2">
                      <cat.icon className={`w-4 h-4 ${cat.color}`} />
                      {cat.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Stock Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredItems.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Geen voorraad gevonden</p>
                <p className="text-sm mt-1">Voeg producten toe via het tabblad "Producten Toevoegen"</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Categorie</TableHead>
                    <TableHead className="text-center">Voorraad</TableHead>
                    <TableHead className="text-center">Min.</TableHead>
                    <TableHead>Locatie</TableHead>
                    <TableHead className="text-right">Acties</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((item) => {
                    const Icon = getCategoryIcon(item.product_category);
                    const isLow = item.is_low_stock;
                    const isOut = item.quantity === 0;
                    
                    return (
                      <TableRow key={item.product_id} data-testid={`stock-row-${item.product_id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{item.product_name}</p>
                            {item.product_brand && (
                              <p className="text-xs text-muted-foreground">{item.product_brand}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="gap-1">
                            <Icon className={`w-3 h-3 ${getCategoryColor(item.product_category)}`} />
                            {CATEGORIES.find(c => c.value === item.product_category)?.label || item.product_category}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`font-bold text-lg ${isOut ? 'text-red-500' : isLow ? 'text-amber-500' : 'text-foreground'}`}>
                            {item.quantity}
                          </span>
                          <span className="text-xs text-muted-foreground ml-1">{item.product_unit}</span>
                          {isOut && <Badge variant="destructive" className="ml-2 text-xs">Op</Badge>}
                          {isLow && !isOut && <Badge variant="outline" className="ml-2 text-xs border-amber-500 text-amber-500">Laag</Badge>}
                        </TableCell>
                        <TableCell className="text-center text-muted-foreground">
                          {item.min_quantity}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {item.location || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-green-500 hover:text-green-600 hover:bg-green-500/10"
                              onClick={() => handleOpenMovement(item, 'in')}
                              title="Voorraad verhogen"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                              onClick={() => handleOpenMovement(item, 'out')}
                              title="Voorraad verlagen"
                              disabled={item.quantity === 0}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleOpenEdit(item)}
                              title="Bewerken"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleOpenHistory(item)}
                              title="Geschiedenis"
                            >
                              <History className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-500" />
                Voorraad Alerts
              </CardTitle>
              <CardDescription>
                Producten die bijbesteld moeten worden
              </CardDescription>
            </CardHeader>
            <CardContent>
              {stockItems.filter(item => item.is_low_stock).length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Geen voorraad alerts</p>
                  <p className="text-sm mt-1">Alle producten hebben voldoende voorraad</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {stockItems
                    .filter(item => item.is_low_stock)
                    .sort((a, b) => a.quantity - b.quantity)
                    .map((item) => {
                      const Icon = getCategoryIcon(item.product_category);
                      const isOut = item.quantity === 0;
                      return (
                        <div
                          key={item.product_id}
                          className={`p-4 rounded-lg border ${isOut ? 'border-red-500/50 bg-red-500/5' : 'border-amber-500/50 bg-amber-500/5'}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-md ${isOut ? 'bg-red-500/20' : 'bg-amber-500/20'} flex items-center justify-center`}>
                                <Icon className={`w-5 h-5 ${isOut ? 'text-red-400' : 'text-amber-400'}`} />
                              </div>
                              <div>
                                <p className="font-medium">{item.product_name}</p>
                                <p className="text-xs text-muted-foreground">{item.product_brand}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className={`text-lg font-bold ${isOut ? 'text-red-500' : 'text-amber-500'}`}>
                                  {item.quantity} / {item.min_quantity}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {isOut ? 'Niet op voorraad' : 'Onder minimum'}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => handleOpenMovement(item, 'in')}
                                className="bg-green-600 hover:bg-green-500"
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                Bijvullen
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="add" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Producten Toevoegen aan Voorraad</CardTitle>
              <CardDescription>
                Selecteer producten uit je catalogus om toe te voegen aan voorraadbeheer
              </CardDescription>
            </CardHeader>
            <CardContent>
              {productsNotInStock.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Alle producten zijn al toegevoegd aan de voorraad
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {productsNotInStock.map((product) => {
                    const Icon = getCategoryIcon(product.category);
                    return (
                      <Card key={product.id} className="border-dashed">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`w-8 h-8 rounded-md bg-muted flex items-center justify-center`}>
                                <Icon className={`w-4 h-4 ${getCategoryColor(product.category)}`} />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{product.name}</p>
                                <p className="text-xs text-muted-foreground">{product.brand}</p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAddProductToStock(product)}
                              disabled={saving}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Toevoegen
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Movement Dialog */}
      <Dialog open={movementDialogOpen} onOpenChange={setMovementDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {movementType === 'in' ? 'Voorraad Verhogen' : 'Voorraad Verlagen'}
            </DialogTitle>
            <DialogDescription>
              {selectedProduct?.product_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-center gap-4 p-4 bg-muted/50 rounded-lg">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Huidig</p>
                <p className="text-2xl font-bold">{selectedProduct?.quantity || 0}</p>
              </div>
              <div className={`text-2xl ${movementType === 'in' ? 'text-green-500' : 'text-red-500'}`}>
                {movementType === 'in' ? <TrendingUp /> : <TrendingDown />}
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Nieuw</p>
                <p className="text-2xl font-bold">
                  {movementType === 'in' 
                    ? (selectedProduct?.quantity || 0) + movementQuantity
                    : Math.max(0, (selectedProduct?.quantity || 0) - movementQuantity)
                  }
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Aantal</Label>
              <Input
                type="number"
                min="1"
                value={movementQuantity}
                onChange={(e) => setMovementQuantity(parseInt(e.target.value) || 1)}
              />
            </div>

            <div className="space-y-2">
              <Label>Notities (optioneel)</Label>
              <Textarea
                value={movementNotes}
                onChange={(e) => setMovementNotes(e.target.value)}
                placeholder={movementType === 'in' ? 'Bijv. Bestelling #123' : 'Bijv. Installatie Janssen'}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMovementDialogOpen(false)}>
              Annuleren
            </Button>
            <Button 
              onClick={handleSaveMovement} 
              disabled={saving}
              className={movementType === 'in' ? 'bg-green-600 hover:bg-green-500' : 'bg-red-600 hover:bg-red-500'}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {movementType === 'in' ? 'Verhogen' : 'Verlagen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Voorraad Bewerken</DialogTitle>
          </DialogHeader>
          {editingStock && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Huidige Voorraad</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editingStock.quantity}
                    onChange={(e) => setEditingStock({...editingStock, quantity: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Minimum Voorraad</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editingStock.min_quantity}
                    onChange={(e) => setEditingStock({...editingStock, min_quantity: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Locatie</Label>
                <Input
                  value={editingStock.location}
                  onChange={(e) => setEditingStock({...editingStock, location: e.target.value})}
                  placeholder="Bijv. Magazijn, Bestelwagen"
                />
              </div>
              <div className="space-y-2">
                <Label>Notities</Label>
                <Textarea
                  value={editingStock.notes}
                  onChange={(e) => setEditingStock({...editingStock, notes: e.target.value})}
                  rows={2}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Voorraadgeschiedenis</DialogTitle>
            <DialogDescription>
              {historyProduct?.product_name}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            {loadingMovements ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : movements.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Geen bewegingen gevonden
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Datum</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-center">Aantal</TableHead>
                    <TableHead className="text-center">Na</TableHead>
                    <TableHead>Door</TableHead>
                    <TableHead>Notities</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movements.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="text-xs">
                        {formatDate(m.created_at)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={m.movement_type === 'in' ? 'default' : 'destructive'} className="text-xs">
                          {m.movement_type === 'in' ? '+' : '-'}{m.movement_type === 'in' ? 'In' : 'Uit'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center font-medium">
                        <span className={m.movement_type === 'in' ? 'text-green-500' : 'text-red-500'}>
                          {m.movement_type === 'in' ? '+' : '-'}{m.quantity}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {m.quantity_after}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {m.created_by_name || '-'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">
                        {m.notes || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Stock;
